import React, { Reducer, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useAppSelector } from '../../../../Store';
import { colors, css, fonts, frames } from '../../../../Styles';
import { FeedCurveSettingsModal, PageHeader, FeedCurveSpreading, FeedCurveTable, FeedCurveWithInfoBoxes, NewModal, MainContentPaddingTypes, NewButton, Overlay } from '../../../../Components';
import { mediaQueriesNumbers, useMediaQuery } from '../../../../Styles/mediaQueries';
import { Pages } from '../../../../Navigation/root';
import { Graph_QL, DynamicDataObk, useFeedCurves, useMilestones, FeedCurve as FeedCurveValue, MaintainFeedCurveCreates, MaintainFeedCurveDelete, MaintainFeedCurveUpdates, useAnimal, useFeedComponents } from '../../../../Services';
import ShowFeedCurve from '../../../../Components/FeedCurves/Modals/showFeedCurve';
import { FeedCurveContext, FeedCurveDispatcherT, FeedCurveReducer, GraphTypes, IFeedCurveContext } from '../../../../Components/FeedCurves/_FeedCurves/feedCurveContext';
import { ButtonTypes } from '../../../../Definitions';
import ConfirmModal from '../../../../Components/_Components/confirmModal';
import { selectFeedCurveById } from '../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/FeedDataSlices/feedCurvesSlice';
import { useFeedcurveOptions } from '../../../../Components/_Components';

export default function FeedCurve() {
  const { t } = useTranslation();
  const location = useLocation();
  const [disableCurveNavigation, setDisableCurveNavigation] = useState(false);
  const [selectedFeedCurveId, setSelectedFeedCurveId] = useState<number>();
  const [modalState, setModalState] = useState<"ShowFeedCurve" | "Spreading" | "Settings" | "delete" | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined)
  const [editTable, setEditTable] = useState(false);
  
  // Media Quiries
  const mobileXs = useMediaQuery(`(max-width: 690px)`);
  const isScreenHeightMax = useMediaQuery(`(max-height: ${mediaQueriesNumbers.ScreenHeightMax}px)`);

  // Connection
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = useMemo(()=> (Graph_QL.ObjToString({domain: farm ?? ""})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({ domain: farm ?? "" })), [farm]);
  
  // Reducer
  const [_state, dispatch] = useReducer<Reducer<IFeedCurveContext, FeedCurveDispatcherT>>(FeedCurveReducer, {
    graphType: GraphTypes.feedCurveWithInfoBoxes,
    milestone: {
      selectedMilestone: undefined,
      selectedDay: undefined
    },
    component: undefined,
    error: { Elementid: 0, errorText: "" },
  } as IFeedCurveContext);
  
  // #region | Feed Curves data
  const { call: getFeedCurves, data: feedCurveState, maintainEntries: maintainFeedCurveEntries, deleteFeedCurve } = useFeedCurves(connection);
  const { call: getMilestones, data: milestoneState } = useMilestones(connection);
  const { call: getAnimalInfo, data: animalState } = useAnimal(connection);
  const { call: getFeedComponentsInfo, data: feedComponentsState } = useFeedComponents(connection);
  
  const maintainFeedCurve = useCallback((changed?: Partial<DynamicDataObk<MaintainFeedCurveUpdates>>, deleted?: Partial<DynamicDataObk<MaintainFeedCurveDelete>>, created?: Partial<DynamicDataObk<MaintainFeedCurveCreates>>, feedCurve?: {originalFeedCurve: FeedCurveValue, optimisticFeedCurve: FeedCurveValue}) => {
    const returnValue: Promise<[boolean, string]> = new Promise(() =>  [false, "Internal error"]); 
    if (maintainFeedCurveEntries) {
      return maintainFeedCurveEntries(changed, deleted, created, feedCurve) ?? returnValue; 
    }
    return returnValue
  },[maintainFeedCurveEntries])
  
  const fetchedFeedCurves = useMemo(() => {
    return feedCurveState && feedCurveState[domain] ? feedCurveState[domain].data.filter((feedCurve) => feedCurve.info?.deleted !== true) : [];
  },[domain, feedCurveState])

  
  const feedCurve = useAppSelector((state) => {if (selectedFeedCurveId) {return selectFeedCurveById(state, selectedFeedCurveId)}})
  const milestoneOptions = useMemo(() => {
    return milestoneState[domain]?.data.filter((milestone) => !milestone.deleted).map((milestone) => ({label: milestone.name, id: milestone.milestone_id})) ?? []
  },[domain, milestoneState]) 

  useEffect(() => {
    if (!feedCurve && selectedFeedCurveId) {
      getFeedCurves({ids: [selectedFeedCurveId], ifFeedCurveBaseInfoOnly: true})
    }
    getAnimalInfo({})
    getFeedComponentsInfo({fetchAmount: 50}, connection);
  },[connection, feedCurve, getFeedComponentsInfo, getFeedCurves, selectedFeedCurveId])

  useEffect(() => {
    if (!(milestoneState[domain])) {
      getMilestones({fetchAmount: 50})
    }
  }, [domain, getMilestones, milestoneState])
  
  const animals = useMemo(() => {
    return (animalState[domain]?.data || []).filter(a => a.feedcurve_id === feedCurve?.feedCurveId);
  }, [animalState, domain, feedCurve]);

  const usedComponentIds = useMemo(() => feedCurve?.components?.map((component) => component.componentId),[feedCurve?.components]);
  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data.filter((feedComponent) => !feedComponent.deleted && !usedComponentIds?.includes(feedComponent.id)): undefined,[domain, feedComponentsState, usedComponentIds])
  const allFeedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data.filter((feedComponent) => !feedComponent.deleted): undefined,[domain, feedComponentsState])

  useEffect(() => {
    if (!selectedFeedCurveId) {
      return
    }
    const containsSettings = (feedCurve?.milestones && feedCurve.milestones.length === 0) || Boolean(feedCurve?.milestones?.find((milestone) => milestone.feedPlan))
    if (!feedCurve || !feedCurve.milestones || !containsSettings) {
      getFeedCurves({ids: [selectedFeedCurveId], ifSpreading: true})
    }
    
  },[feedCurve, getFeedCurves, selectedFeedCurveId]);

  // Get options from feedCurve Data and return the first to view if there isn't any selected from the feedCurves page.
  const {feedCurveOptions} = useFeedcurveOptions();
  
  useEffect(() => {
    if (selectedFeedCurveId !== undefined) {
      return
    }
    if (location.state?.params?.selected !== undefined) {
      setSelectedFeedCurveId(location.state.params.selected)
      return
    }
    if (feedCurveOptions.length && feedCurveOptions[0]) {
      setSelectedFeedCurveId(feedCurveOptions[0].id)
    } 
  },[feedCurveOptions, location.state?.params?.selected, selectedFeedCurveId])
  
  useEffect(() => {
    if (location.state?.params?.selected && !selectedFeedCurveId) {
      setSelectedFeedCurveId(location.state.params.selected);
    }
  }, [location.state, selectedFeedCurveId, domain]);
  // #endregion

  const currentIndex = feedCurveOptions.findIndex(
    (feedCurve) => feedCurve.id === selectedFeedCurveId
  );

  const handleNext = () => {
    if (!selectedFeedCurveId || currentIndex === -1) return;
    const nextIndex = (currentIndex + 1) % feedCurveOptions.length;
    const nextFeedCurveId = feedCurveOptions[nextIndex].id;
    setSelectedFeedCurveId(nextFeedCurveId);
  };
  
  const handlePrev = () => {
    if (!selectedFeedCurveId || currentIndex === -1) return;
    const prevIndex = (currentIndex - 1 + feedCurveOptions.length) % feedCurveOptions.length;
    const prevFeedCurveId = feedCurveOptions[prevIndex].id;
    setSelectedFeedCurveId(prevFeedCurveId);
  };

  return (
    <frames.Page_inner>
      {editTable ? <Overlay overrides={{  }} beforeUnload onClickAlert /> : undefined}
      <PageHeader
        zIndex={99}
        topHeader={t(`menuHeaders.${Pages.feedCurves}`, { defaultValue: "feed curves" })}
        subSelect={{
          onNext: handleNext,
          onPrev: handlePrev,
          options: feedCurveOptions,
          onChange: (e) => setSelectedFeedCurveId(Number(e?.id ?? 0)),
          defaultValue: feedCurveOptions.find(option => option.label === feedCurve?.name)
        }}
        optionButtons={{
          contents: [
            t("feedCurvePages.feedCurve.spreading", {defaultValue: "spreading"}), 
            // t("feedCurvePages.feedCurve.corrections", {defaultValue: "corrections"}), 
            t("feedCurvePages.feedCurve.settings", { defaultValue: "settings" }),
            t("feedCurvePages.feedCurve.deleteFeedcurve", {defaultValue: "delete feedcurve"})
          ],
          onClick: (i => {
            if (i === 0) {setModalState("Spreading")} 
            else if (i === 1) { setModalState("Settings") }
            else if (i === 2) { setModalState("delete") }
          })
        }}
      />
      <frames._page_inner_content>
        <FeedCurveContext.Provider value={{ state: _state, dispatch }}>
        <div style={{display: "grid", overflow: "hidden", height: "100%", width: "100%", gridTemplateColumns: "1fr", gridTemplateRows: (isScreenHeightMax || mobileXs) ? "1fr" : "350px 1fr"}}>
          {/* FeedCurveViewer */}
          {(isScreenHeightMax || mobileXs) ? <></>
          : <div style={{gridArea: "1/1/2/2", overflow: "hidden", display: "flex", alignItems: "center"}}>
              {feedCurve?.milestones &&
                <FeedCurveWithInfoBoxes animals={animals} data={feedCurve} invertedColors/> 
            }
          </div>}

          {/* FeedCurveTable */}
          <div style={{ gridArea: (isScreenHeightMax || mobileXs) ? "1/1/2/2" : "2/1/3/2", overflow: "hidden", position: "relative" }}>
            {feedCurve && 
              <FeedCurveTable 
                onEditTable={setEditTable}
                zIndex={editTable ? 9999 : undefined}
                milestoneOptions={milestoneOptions}
                setDisableCurveNavigation={setDisableCurveNavigation}
                watchFeedCurve={() => setModalState("ShowFeedCurve")}
                feedCurve={feedCurve}
                maintainFeedCurve={maintainFeedCurve}
                allFeedComponents={allFeedComponents}
                feedComponents={feedComponents}
              />
            }
          </div>
          </div>
        </FeedCurveContext.Provider>
      </frames._page_inner_content>
      

      {feedCurve && modalState === "ShowFeedCurve" && farm &&
        <ShowFeedCurve
          farm={farm}
          feedCurve={feedCurve}
          handleClose={() => { setModalState(undefined) }}
        />
      }
      
      {feedCurve && modalState === "Spreading" && 
        <FeedCurveSpreading feedCurve={feedCurve} onClose={() => setModalState(undefined)}/>
      }

      {feedCurve && modalState === "Settings" && 
        <FeedCurveSettingsModal closeModal={() => setModalState(undefined)} feedCurve={feedCurve} />
      }


      {feedCurve && modalState === "delete" && 
        <ConfirmModal
        disclaimer={`${t("feedCurvePages.feedCurve.confirmDelete", {defaultValue: "Are you sure you want to delete this feedcurve"})}?`}
        message={message}
        onCancel={() => setModalState(undefined)}
        onConfirm={async () => {
          if (deleteFeedCurve) {
            const res = await deleteFeedCurve(feedCurve.feedCurveId);

            console.log(res)

            if (res.success) {
              setModalState(undefined)
              setSelectedFeedCurveId(feedCurveOptions[((feedCurveOptions.findIndex(f => f.id === selectedFeedCurveId))+1)%feedCurveOptions.length].id)
            }
            if (!res.success) {
              setMessage(res.message)
              setTimeout(() => {
                setMessage(undefined);
              }, 2000);
            }
          }
        }}
        />
      }
    
    </frames.Page_inner>
  );
}